import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Car, 
  Shield, 
  Phone, 
  MapPin, 
  Users, 
  AlertTriangle,
  CheckCircle,
  Lock,
  Eye,
  ArrowLeft,
  Heart,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';

const Safety = () => {
  const safetyFeatures = [
    {
      icon: Shield,
      title: "Verified Drivers",
      description: "Every driver goes through a legit background check before they can pick up their first rider. No shortcuts, no exceptions."
    },
    {
      icon: MapPin,
      title: "Share Your Trip",
      description: "Send your ride details to your squad with one tap. They can follow along until you're safe at your destination."
    },
    {
      icon: Phone,
      title: "We're Always Up",
      description: "24/7 support because sketchy situations don't check the clock. One tap connects you to help, anytime."
    },
    {
      icon: AlertTriangle,
      title: "SOS Button",
      description: "Emergency button sends your location to your contacts and our safety team instantly. We've got your back."
    },
    {
      icon: Eye,
      title: "Trip Monitoring",
      description: "Our tech keeps an eye on rides for weird stops or route changes. If something's off, we check in."
    },
    {
      icon: Lock,
      title: "No Cash Needed",
      description: "Everything's handled in the app. No awkward cash exchanges, no worrying about having the right bills."
    }
  ];

  const safetyTips = [
    "Check the driver's name, photo, and license plate before you hop in",
    "Share your trip with a friend—they'll appreciate the peace of mind",
    "Back seat = easy exit. Just a pro tip.",
    "Trust your gut. If something feels off, cancel and get a new ride",
    "Keep your phone charged during rides (you already knew this one)",
    "Rate your driver honestly—it helps keep the community solid"
  ];

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center transform -skew-x-6">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-['Barlow_Condensed'] font-black text-white">REDDI</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-400 hover:text-white gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-8">
            <Shield className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-4xl md:text-6xl font-['Barlow_Condensed'] font-black text-white uppercase mb-6">
            Your Safety = Our Priority
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Look, we get it—getting into a stranger's car is a leap of faith. 
            That's why we've built safety into every part of Reddi. Real features, not just promises.
          </p>
        </div>
      </section>

      {/* Safety Features Grid */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4 text-center">
            Built-In Protection
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            Not just checkboxes—actual features that actually work.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {safetyFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-slate-800/50 border border-slate-700 p-8 hover:border-red-500/50 transition-colors"
              >
                <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Tips */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-12 justify-center">
            <Sparkles className="w-8 h-8 text-amber-500" />
            <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase">
              Quick Safety Tips
            </h2>
          </div>
          <div className="space-y-4">
            {safetyTips.map((tip, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-4 bg-slate-800/30 border border-slate-700/50"
              >
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-slate-300">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Standards */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <Heart className="w-12 h-12 text-red-500 mx-auto mb-6" />
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-6">
            Respect Goes Both Ways
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Reddi only works when everyone's cool to each other. Riders and drivers both rate each trip, 
            and that feedback helps keep the community positive. Be the kind of rider or driver you'd want to meet.
          </p>
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-red-600/20 border border-red-600/30 rounded-lg">
            <Users className="w-5 h-5 text-red-500" />
            <span className="text-red-400 font-medium">Building a community we're proud of</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-6">
            Ready to Ride?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register?type=rider">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                Get Started
              </Button>
            </Link>
            <Link to="/help">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          © 2025 REDDI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Safety;
